// src/components/eventos/components/diretor/MetricasGerais.js
import React, { useMemo } from 'react';

const MetricasGerais = ({ eventos }) => {
    const metricas = useMemo(() => {
        const agora = new Date();
        const mesAtual = agora.getMonth(), anoAtual = agora.getFullYear();
        const eventosAtivos = eventos.filter(e => e.status === 'agendado' || e.status === 'em_andamento');
        const eventosConcluidos = eventos.filter(e => e.status === 'concluido');
        const eventosDoMes = eventos.filter(e => {
            const d = new Date(e.data);
            return d.getMonth() === mesAtual && d.getFullYear() === anoAtual;
        });
        const totalTarefas = eventos.reduce((a, e) => a + (e.tarefas?.length || 0), 0);
        const tarefasConcluidas = eventos.reduce((a, e) => a + (e.tarefas?.filter(t => t.status === 'concluida').length || 0), 0);
        const taxaConclusao = totalTarefas > 0 ? Math.round((tarefasConcluidas / totalTarefas) * 100) : 0;

        const eventosPontuais = eventosConcluidos.filter(ev => ev.tarefas?.every(t => {
            if (!t.horaFimReal || !t.horaFim) return true;
            const [hP, mP] = t.horaFim.split(':').map(Number);
            const [hR, mR] = t.horaFimReal.split(':').map(Number);
            return (hR * 60 + mR) <= (hP * 60 + mP + 5);
        }));
        const taxaPontualidade = eventosConcluidos.length > 0 ? Math.round((eventosPontuais.length / eventosConcluidos.length) * 100) : 0;

        const duracaoTotal = eventosConcluidos.reduce((a, ev) => {
            if (!ev.hora_inicio_real || !ev.hora_fim_real) return a;
            const [hI, mI] = ev.hora_inicio_real.split(':').map(Number);
            const [hF, mF] = ev.hora_fim_real.split(':').map(Number);
            return a + ((hF * 60 + mF) - (hI * 60 + mI));
        }, 0);
        const mediaDuracao = eventosConcluidos.length > 0 ? Math.round(duracaoTotal / eventosConcluidos.length) : 0;

        return { eventosAtivos: eventosAtivos.length, eventosConcluidos: eventosConcluidos.length, eventosDoMes: eventosDoMes.length, totalTarefas, tarefasConcluidas, taxaConclusao, taxaPontualidade, mediaDuracao };
    }, [eventos]);

    const perf = (v, high, mid) => v >= high ? '#2CCB90' : v >= mid ? '#d97706' : '#ef4444';
    const perfBg = (v, high, mid) => v >= high ? 'rgba(44,203,144,0.08)' : v >= mid ? 'rgba(217,119,6,0.08)' : 'rgba(239,68,68,0.06)';
    const perfBorder = (v, high, mid) => v >= high ? 'rgba(44,203,144,0.18)' : v >= mid ? 'rgba(217,119,6,0.18)' : 'rgba(239,68,68,0.15)';

    const cards = [
        { label: 'Eventos Ativos', value: metricas.eventosAtivos, sub: 'Agendados e em andamento', color: '#2563eb', bg: 'rgba(37,99,235,0.06)', border: 'rgba(37,99,235,0.15)', badge: metricas.eventosAtivos > 0 ? 'EM AÇÃO' : null, badgeColor: '#2563eb' },
        { label: 'Eventos Concluídos', value: metricas.eventosConcluidos, sub: 'Total histórico', color: '#2CCB90', bg: 'rgba(44,203,144,0.06)', border: 'rgba(44,203,144,0.15)' },
        { label: 'Eventos Este Mês', value: metricas.eventosDoMes, sub: new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }), color: '#7c3aed', bg: 'rgba(139,92,246,0.06)', border: 'rgba(139,92,246,0.15)' },
        { label: 'Taxa de Conclusão', value: `${metricas.taxaConclusao}%`, sub: `${metricas.tarefasConcluidas}/${metricas.totalTarefas} tarefas`, color: perf(metricas.taxaConclusao, 90, 70), bg: perfBg(metricas.taxaConclusao, 90, 70), border: perfBorder(metricas.taxaConclusao, 90, 70) },
        { label: 'Pontualidade', value: `${metricas.taxaPontualidade}%`, sub: 'Eventos no horário', color: perf(metricas.taxaPontualidade, 80, 60), bg: perfBg(metricas.taxaPontualidade, 80, 60), border: perfBorder(metricas.taxaPontualidade, 80, 60) },
        { label: 'Duração Média', value: metricas.mediaDuracao > 0 ? `${Math.floor(metricas.mediaDuracao / 60)}h ${metricas.mediaDuracao % 60}m` : '--', sub: 'Tempo médio de evento', color: '#2DB8E1', bg: 'rgba(45,184,225,0.06)', border: 'rgba(45,184,225,0.15)' },
    ];

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px', fontFamily: "'Inter', -apple-system, sans-serif" }}>
            {cards.map((card, i) => (
                <div key={i} style={{ background: card.bg, border: `1px solid ${card.border}`, borderRadius: '14px', padding: '20px', transition: 'transform 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'none'}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{card.label}</p>
                        {card.badge && (
                            <span style={{ padding: '2px 8px', borderRadius: '100px', background: `${card.badgeColor}18`, border: `1px solid ${card.badgeColor}35`, color: card.badgeColor, fontSize: '10px', fontWeight: 700 }}>{card.badge}</span>
                        )}
                    </div>
                    <p style={{ margin: '0 0 4px', fontSize: '36px', fontWeight: 900, color: card.color, letterSpacing: '-0.02em', lineHeight: 1 }}>{card.value}</p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>{card.sub}</p>
                </div>
            ))}
        </div>
    );
};

export default MetricasGerais;
